import { BreifInfluencerV2StatusEnum, SORT_ORDER } from "@entities/brief/lib/constants";
import { useGetBriefInfluencersV2Paginated } from "@entities/brief/lib/hooks/useGetBriefInfluencersV2Paginated";
import { SelectContact } from "@entities/brief/ui/Influencer/v2/SelectContact";
import useGetPricingPocFacets from "@entities/pricingWorkload/lib/hooks/useGetPricingPocFacets";
import { ContactFilterValue } from "@features/BriefContactFilter/BriefContactFilterV2";
import { SocialMediaInfluencerComp } from "@features/socialMediaInfluencer/SocialMediaInfluencerComp";
import { IBrief } from "@shared/lib/types/brief";
import { IBriefInfluencerV2 } from "@shared/lib/types/briefInfluencerV2";
import { Input, Modal, Table, TableTitleLayout, Typography } from "@shared/ui";
import { Drawer, Flex } from "antd";
import classNames from "classnames";
import { PackageCheck, Search } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDebounce } from "use-debounce";
import {
  ContactsModalState,
  useClosureDetailColumns
} from "../hooks/useClosureDetailColumns";
import { CLOSURE_INFLUENCER_TABS } from "../lib/constants";
import { getClosureInfluencerFilters } from "../lib/getClosureInfluencerFilters";
import { Tabs } from "../lib/types";
import styles from "./styles.module.scss";

const { Text, Title } = Typography;

type ClosureDetailCreatorsSectionProps = {
  briefId: string;
  userId: string;
  brief?: IBrief;
};

export function ClosureDetailCreatorsSection({
  briefId,
  userId,
  brief,
}: ClosureDetailCreatorsSectionProps) {
  const [offerStatus, setOfferStatus] = useState<Tabs>(Tabs.All);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery] = useDebounce(searchQuery, 1000);
  const [sortBy, setSortBy] = useState<
    | {
        sortByDynamicFields?: {
          [key: string]: SORT_ORDER;
        };
      }
    | undefined
  >(undefined);

  const [contactFilter, setContactFilter] = useState<ContactFilterValue>({
    primaryContact: { type: [], agencyId: [] },
  });

  const [deliverableFilter, setDeliverableFilter] = useState<string[]>([]);

  const isContactFilterActive =
    contactFilter.isCreatorOnboardingPending === true ||
    contactFilter.primaryContact.type.length > 0 ||
    contactFilter.primaryContact.agencyId.length > 0;

  const [dynamicFields, setDynamicFields] = useState<{
    [key: string]: {
      equalTo?: string[];
      range?: {
        from: number;
        to: number;
      };
    };
  }>({});

  const setDynamicField = (
    field: string,
    value?: string[] | { from: number; to: number }
  ) => {
    if (value) {
      const existingField = dynamicFields[field];
      const isArray = Array.isArray(value);
      const isRange = !isArray && "from" in value && "to" in value;
      setDynamicFields({
        ...dynamicFields,
        [field]: {
          equalTo: isArray ? value : existingField?.equalTo || undefined,
          range: isRange ? value : existingField?.range || undefined,
        },
      });
    } else {
      const newDynamicFields = { ...dynamicFields };
      delete newDynamicFields[field];
      setDynamicFields(newDynamicFields);
    }
  };

  const [contactsModalProps, setContactsModalProps] =
    useState<ContactsModalState>({
      visible: false,
      briefInfluencerId: "",
      creatorInfo: undefined,
      conatcts: [],
      addContactOnly: false,
      initialContact: null,
    });

  const [selectedInfluencer, setSelectedInfluencer] = useState<{
    visible: boolean;
    props?: {
      influencerId?: string;
      username?: string;
      platform?: string;
      toOpenInfluencer?: boolean;
      creatorInfo: {
        name?: string;
        image?: string;
        username?: string;
      };
    };
  }>({ visible: false });

  const {
    pricingPocFacets,
    isPricingPocFacetsLoading,
    refetchPricingPocFacets,
  } = useGetPricingPocFacets(briefId || "", userId || "");

  const filters = useMemo(
    () =>
      getClosureInfluencerFilters({
        briefId,
        userId,
        page,
        limit: pageSize,
        searchQuery: debouncedSearchQuery,
        offerStatus,
        contactFilter,
        deliverableFilter,
        dynamicFields,
        sortByDynamicFields: sortBy?.sortByDynamicFields,
      }),
    [
      briefId,
      userId,
      page,
      pageSize,
      debouncedSearchQuery,
      offerStatus,
      contactFilter,
      deliverableFilter,
      dynamicFields,
      sortBy?.sortByDynamicFields,
    ]
  );

  const { briefInfluencers, isFetching, refetch, totalCount } =
    useGetBriefInfluencersV2Paginated(filters, Boolean(briefId && userId));

  useEffect(() => {
    setContactFilter({ primaryContact: { type: [], agencyId: [] } });
    setDeliverableFilter([]);
  }, [debouncedSearchQuery, offerStatus]);

  useEffect(() => {
    setPage(1);
  }, [
    userId,
    offerStatus,
    debouncedSearchQuery,
    contactFilter,
    deliverableFilter,
    dynamicFields,
    sortBy,
  ]);

  const handleActionSuccess = useCallback(() => {
    refetch();
    refetchPricingPocFacets();
  }, [refetch, refetchPricingPocFacets]);

  const columns = useClosureDetailColumns({
    briefId,
    briefInfluencers,
    onActionSuccess: handleActionSuccess,
    setContactsModalProps,
    setSelectedInfluencer,
    sortBy,
    setSortBy,
    dynamicFields,
    setDynamicField,
    contactFilter,
    setContactFilter,
    isContactFilterActive,
    deliverableFilter,
    setDeliverableFilter,
  });

  useEffect(() => {
    refetch();
  }, [offerStatus]);

  const activeTabMeta = CLOSURE_INFLUENCER_TABS.find(
    (tab) => tab.value === offerStatus
  );

  const handlePaginationChange = (nextPage: number, nextPageSize: number) => {
    setPage(nextPage);
    setPageSize(nextPageSize);
  };

  const isDeclined = useCallback((record: IBriefInfluencerV2) => {
      return (
        record.isBrandDeclined ||
        record.isCreatorDeclined ||
        record.status === BreifInfluencerV2StatusEnum.Deleted
      );
    }, []);

  return (
    <section className={styles.creatorsSection}>
      <div className={styles.tableCard}>
        <div className={styles.tableCardHeader}>
          <div className={styles.tableCardTitleWrap}>
            <div className={styles.tableCardIcon}>
              <PackageCheck size={18} />
            </div>
            <div>
              <Title level={5} className={styles.tableCardTitle}>
                Creators on this project
              </Title>
              <Text type="secondary" className={styles.tableCardDescription}>
                {activeTabMeta?.description}
              </Text>
            </div>
          </div>
        </div>

        <Table<IBriefInfluencerV2>
          rowKey="_id"
          columns={columns}
          dataSource={briefInfluencers}
          title={() => <TableTitleLayout 
            filterComponent={<Flex gap={10}>
              <Input
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          placeholder="Creator handle, contact"
          allowClear
          prefix={<Search size={14}/>}
          style={{width: 300}}
        />

        <div className={styles.tabList} role="tablist">
            {CLOSURE_INFLUENCER_TABS.map((tab) => {
              const isActive = offerStatus === tab.value;

              return (
                <button
                  key={tab.value}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  className={`${styles.tabButton} ${isActive ? styles.tabButtonActive : ""}`}
                  onClick={() => setOfferStatus(tab.value)}
                >
                  {tab.label} (
                  {pricingPocFacets?.data?.myClosures?.[tab.value] || 0})
                </button>
              );
            })}
          </div>
            </Flex>}
            />}
          loading={isFetching}
          tableLayout="fixed"
          className={styles.briefsTable}
          scroll={{ x: 1300 }}
          pagination={{
            current: page,
            pageSize,
            total: totalCount,
            onChange: handlePaginationChange,
          }}
          rowClassName={(record) => {
                        return classNames(styles.tableRow, {
                          [styles.rowDeclined]:
                            isDeclined(record),
                          [styles.rowPurchased]:
                            record.status === BreifInfluencerV2StatusEnum.Purchased,
                        });
                      }}
          locale={{
            emptyText: debouncedSearchQuery.trim()
              ? `No creators match "${debouncedSearchQuery.trim()}" in this view.`
              : (activeTabMeta?.empty ?? "No creators found"),
          }}
        />
      </div>

      <Modal
        title="Verify Contact"
        open={contactsModalProps.visible}
        onCancel={() =>
          setContactsModalProps({
            visible: false,
            briefInfluencerId: "",
            creatorInfo: undefined,
            conatcts: [],
            initialContact: null,
          })
        }
        footer={null}
        width={800}
        destroyOnClose
      >
        {contactsModalProps.visible && contactsModalProps.creatorInfo ? (
          <SelectContact
            initialContact={contactsModalProps.initialContact}
            briefInfluencerId={contactsModalProps.briefInfluencerId}
            onSuccess={() => {
              refetch();
            }}
            onClose={() =>
              setContactsModalProps({
                visible: false,
                briefInfluencerId: "",
                creatorInfo: undefined,
                conatcts: [],
                addContactOnly: false,
                initialContact: null,
              })
            }
            creatorInfo={contactsModalProps.creatorInfo}
          />
        ) : null}
      </Modal>

      <Drawer
        title="Influencer Details"
        open={selectedInfluencer.visible}
        width={1300}
        footer={null}
        destroyOnClose
        onClose={() => setSelectedInfluencer({ visible: false })}
      >
        {selectedInfluencer.props?.creatorInfo ? (
          <SocialMediaInfluencerComp
            creatorInfo={selectedInfluencer.props.creatorInfo}
            socialMediaInfluencerReq={
              selectedInfluencer.props.influencerId
                ? {
                    toOpenInfluencer: selectedInfluencer.props.toOpenInfluencer,
                    influencerId: selectedInfluencer.props.influencerId,
                  }
                : {
                    username: {
                      platform: selectedInfluencer.props.platform || "",
                      socialMediaUsername:
                        selectedInfluencer.props.username || "",
                    },
                    toOpenInfluencer: selectedInfluencer.props.toOpenInfluencer,
                  }
            }
          />
        ) : null}
      </Drawer>
    </section>
  );
}
